import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { WrappedSpinner } from 'spinner';
import {
  usePersonalBusinessData,
  usePersonalBusinessClassification,
  usePersonalBusinessFilters,
  usePersonalBusinessTable,
} from './hooks';
import { DataEntry, DataEntryRowDataType } from './types';
import { Container, EmptyState } from './PersonalBusinessReviewView.styles';
import { SortingState, RowSelectionState, OnChangeFn } from '@tanstack/react-table';
import {
  PersonalBusinessReviewHeader,
  PersonalBusinessReviewFilters,
  PersonalBusinessReviewStatusBar,
  PersonalBusinessReviewTable,
  VirtualizedPersonalBusinessReviewTable,
  ErrorBoundary,
  TableErrorFallback,
  FiltersErrorFallback,
  StatusBarErrorFallback,
} from './components';
import { logError } from '../../../utils/errorHandling';

type PersonalBusinessReviewViewProps = {
  clientId: string;
  startDate: Date;
  endDate: Date;
  onTransactionCountChange?: (count: number) => void;
};

export const PersonalBusinessReviewView: React.FC<PersonalBusinessReviewViewProps> = ({
  clientId,
  startDate,
  endDate,
  onTransactionCountChange,
}) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [sorting, setSorting] = useState<SortingState>([{ id: 'date', desc: true }]);
  const [pendingClassifications, setPendingClassifications] = useState<Set<string>>(new Set());

  const { personalBusinessData, loading, error, updateQuery } = usePersonalBusinessData({
    clientId,
    startDate,
    endDate,
  });

  const {
    filterState,
    handleFilterChange,
    handleResetFilters,
    isIncludedWithFilters,
    searchInputRef,
  } = usePersonalBusinessFilters({
    startDate,
    endDate,
  });

  const personalBusinessDetails =
    personalBusinessData?.personalBusinessClassificationDetailsByClient;

  const {
    classifyTransactions,
    confirmBusinessClassifications,
    confirmPersonalClassifications,
    classifyLoading,
    confirmLoading,
    handleOptimisticUpdate,
  } = usePersonalBusinessClassification({
    clientId,
    searchMarker: personalBusinessDetails?.dataEntries?.searchMarker,
    updateQuery,
  });

  useEffect(() => {
    if (onTransactionCountChange && personalBusinessDetails) {
      onTransactionCountChange(personalBusinessDetails.unclassifiedEntriesCount);
    }
  }, [personalBusinessDetails, onTransactionCountChange]);

  const { rowData, totalEntries, filteredTotal } = useMemo(() => {
    if (
      loading ||
      !personalBusinessDetails ||
      personalBusinessDetails?.dataEntries.pageInfo.hasNextPage
    )
      return { rowData: [], totalEntries: 0, filteredTotal: 0 };

    const data: DataEntry[] = [];

    personalBusinessDetails?.dataEntries?.dataEntries.forEach(dataEntry => {
      if (dataEntry?.journalEntry?.classification.state !== 'PENDING') return;
      data.push(dataEntry);
    });

    const totalEntries = data.length;
    const rowData: DataEntryRowDataType[] = [];

    data.forEach(dataEntry => {
      if (isIncludedWithFilters(dataEntry)) {
        rowData.push({
          id: dataEntry.id,
          date: new Date(dataEntry.journalEntry.dateOfTransaction),
          description: dataEntry.journalEntry.vendorName,
          ledgerAccount: dataEntry.ledgerAccount,
          classification: dataEntry.journalEntry.classification,
          amount: dataEntry.amount,
        });
      }
    });

    const filteredTotal = rowData.length;
    return { rowData, totalEntries, filteredTotal };
  }, [personalBusinessDetails, loading, isIncludedWithFilters]);

  const { businessIds, personalIds, businessCount, personalCount } = useMemo(() => {
    const businessIds = new Set<string>();
    const personalIds = new Set<string>();
    let businessCount = 0;
    let personalCount = 0;

    for (const row of rowData) {
      if (row.classification.value === 'BUSINESS') {
        businessIds.add(row.id);
        businessCount++;
      } else if (row.classification.value === 'PERSONAL' || !row.classification.value) {
        personalIds.add(row.id);
        personalCount++;
      }
    }

    return { businessIds, personalIds, businessCount, personalCount };
  }, [rowData]);

  const { selectedCount, selectedBusinessCount, selectedPersonalCount } = useMemo(() => {
    const selectedRowIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
    const selectedCount = selectedRowIds.length;
    let selectedBusinessCount = 0;
    let selectedPersonalCount = 0;

    selectedRowIds.forEach(id => {
      const row = rowData.find(r => r.id === id);
      if (row) {
        if (row.classification.value === 'BUSINESS') {
          selectedBusinessCount++;
        } else if (row.classification.value === 'PERSONAL' || !row.classification.value) {
          selectedPersonalCount++;
        }
      }
    });

    return { selectedCount, selectedBusinessCount, selectedPersonalCount };
  }, [rowSelection, rowData]);

  const handleRowClick = useCallback((row: DataEntryRowDataType) => {
    setRowSelection(prev => ({
      ...prev,
      [row.id]: !prev[row.id],
    }));
  }, []);

  const handleMarkAsBusiness = useCallback(async () => {
    const selectedRowIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
    if (selectedRowIds.length === 0) return;

    const personalRowIds = selectedRowIds.filter(id => {
      const row = rowData.find(r => r.id === id);
      return row && (row.classification.value === 'PERSONAL' || !row.classification.value);
    });

    if (personalRowIds.length === 0) return;

    setPendingClassifications(prev => {
      const newSet = new Set(prev);
      personalRowIds.forEach(rowId => {
        newSet.add(rowId);
      });
      return newSet;
    });

    personalRowIds.forEach(rowId => {
      const row = rowData.find(r => r.id === rowId);
      if (row) {
        handleOptimisticUpdate(row, 'BUSINESS');
      }
    });

    try {
      await classifyTransactions(personalRowIds, 'BUSINESS');
    } catch (error) {
      personalRowIds.forEach(rowId => {
        const row = rowData.find(r => r.id === rowId);
        if (row) {
          handleOptimisticUpdate(row, 'PERSONAL');
        }
      });
    } finally {
      setPendingClassifications(prev => {
        const newSet = new Set(prev);
        personalRowIds.forEach(rowId => {
          newSet.delete(rowId);
        });
        return newSet;
      });
    }
  }, [rowSelection, rowData, classifyTransactions, handleOptimisticUpdate]);

  const handleMarkAsPersonal = useCallback(async () => {
    const selectedRowIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
    if (selectedRowIds.length === 0) return;

    const businessRowIds = selectedRowIds.filter(id => {
      const row = rowData.find(r => r.id === id);
      return row && row.classification.value === 'BUSINESS';
    });

    if (businessRowIds.length === 0) return;

    setPendingClassifications(prev => {
      const newSet = new Set(prev);
      businessRowIds.forEach(rowId => {
        newSet.add(rowId);
      });
      return newSet;
    });

    businessRowIds.forEach(rowId => {
      const row = rowData.find(r => r.id === rowId);
      if (row) {
        handleOptimisticUpdate(row, 'PERSONAL');
      }
    });

    try {
      await classifyTransactions(businessRowIds, 'PERSONAL');
    } catch (error) {
      businessRowIds.forEach(rowId => {
        const row = rowData.find(r => r.id === rowId);
        if (row) {
          handleOptimisticUpdate(row, 'BUSINESS');
        }
      });
    } finally {
      setPendingClassifications(prev => {
        const newSet = new Set(prev);
        businessRowIds.forEach(rowId => {
          newSet.delete(rowId);
        });
        return newSet;
      });
    }
  }, [rowSelection, rowData, classifyTransactions, handleOptimisticUpdate]);

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = useCallback(updaterOrValue => {
    setRowSelection(updaterOrValue);
  }, []);

  const { table } = usePersonalBusinessTable({
    data: rowData,
    onRowClick: handleRowClick,
    onRowSelectionChange: handleRowSelectionChange,
    rowSelection,
    sorting,
    onSortingChange: setSorting as OnChangeFn<SortingState>,
  });

  const handleSelectAllBusiness = useCallback(() => {
    setRowSelection(prev => {
      const newSelection = { ...prev };
      businessIds.forEach(id => {
        newSelection[id] = true;
      });
      return newSelection;
    });
  }, [businessIds]);

  const handleSelectAllPersonal = useCallback(() => {
    setRowSelection(prev => {
      const newSelection = { ...prev };
      personalIds.forEach(id => {
        newSelection[id] = true;
      });
      return newSelection;
    });
  }, [personalIds]);

  const handleClearSelection = useCallback(() => {
    setRowSelection({});
  }, []);

  const handleSubmitToBooks = useCallback(async () => {
    if (loading || !personalBusinessDetails) return;

    const selectedRowIds = Object.keys(rowSelection).filter(id => rowSelection[id]);
    if (selectedRowIds.length === 0) return;

    const selectedTransactions = rowData.filter(row => selectedRowIds.includes(row.id));

    const businessRowIds = selectedTransactions
      .filter(row => row.classification.value === 'BUSINESS')
      .map(row => row.id);

    const personalRowIds = selectedTransactions
      .filter(row => row.classification.value === 'PERSONAL' || !row.classification.value)
      .map(row => row.id);

    try {
      if (businessRowIds.length > 0) {
        await confirmBusinessClassifications(businessRowIds, businessRowIds.length);
      }

      if (personalRowIds.length > 0) {
        await confirmPersonalClassifications(personalRowIds, personalRowIds.length);
      }

      setRowSelection({});
    } catch (error: unknown) {
      logError(error as Error, undefined, 'handleSubmitToBooks');
    }
  }, [
    loading,
    personalBusinessDetails,
    rowSelection,
    rowData,
    confirmBusinessClassifications,
    confirmPersonalClassifications,
  ]);

  if (error) {
    logError(error, undefined, 'PersonalBusinessReviewView');
    return (
      <Container>
        <ErrorBoundary fallback={<TableErrorFallback />}>
          <EmptyState>
            <p>There was a problem loading transactions. Please try again.</p>
            <p>{error.message}</p>
          </EmptyState>
        </ErrorBoundary>
      </Container>
    );
  }

  if (loading && !rowData.length) {
    return <WrappedSpinner />;
  }

  return (
    <Container>
      <ErrorBoundary>
        <PersonalBusinessReviewHeader />
      </ErrorBoundary>

      <ErrorBoundary
        fallback={<FiltersErrorFallback />}
        resetKeys={[clientId, personalBusinessDetails?.ledgerAccounts?.length || 0]}
      >
        <PersonalBusinessReviewFilters
          filterState={filterState}
          onFilterChange={handleFilterChange}
          onResetFilters={handleResetFilters}
          searchInputRef={searchInputRef}
          ledgerAccounts={personalBusinessDetails?.ledgerAccounts}
        />
      </ErrorBoundary>

      <ErrorBoundary
        fallback={<StatusBarErrorFallback />}
        resetKeys={[selectedCount, filteredTotal, totalEntries]}
      >
        <PersonalBusinessReviewStatusBar
          filteredTotal={filteredTotal}
          totalEntries={totalEntries}
          personalCount={personalCount}
          businessCount={businessCount}
          selectedCount={selectedCount}
          selectedBusinessCount={selectedBusinessCount}
          selectedPersonalCount={selectedPersonalCount}
          isLoading={loading}
          isClassifyLoading={classifyLoading}
          isConfirmLoading={confirmLoading}
          hasPendingClassifications={pendingClassifications.size > 0}
          onMarkAsBusiness={handleMarkAsBusiness}
          onMarkAsPersonal={handleMarkAsPersonal}
          onSubmitToBooks={handleSubmitToBooks}
          onSelectAllBusiness={handleSelectAllBusiness}
          onSelectAllPersonal={handleSelectAllPersonal}
          onClearSelection={handleClearSelection}
        />
      </ErrorBoundary>

      <ErrorBoundary
        fallback={<TableErrorFallback />}
        resetKeys={[clientId, rowData.length, filterState.search || '']}
      >
        {rowData.length > 500 ? (
          <VirtualizedPersonalBusinessReviewTable
            table={table}
            rowData={rowData}
            pendingClassifications={pendingClassifications}
            onRowClick={handleRowClick}
            searchTerm={filterState.search}
            height={500}
          />
        ) : (
          <PersonalBusinessReviewTable
            table={table}
            rowData={rowData}
            pendingClassifications={pendingClassifications}
            onRowClick={handleRowClick}
            searchTerm={filterState.search}
          />
        )}
      </ErrorBoundary>
    </Container>
  );
};
